import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/core/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/core/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import type { OperatorApp } from '@/features/operator-selection/types/operator-types';

interface OperatorComboboxProps {
  operators: OperatorApp[];
  selected: OperatorApp | null;
  onSelect: (operator: OperatorApp) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

// Operators can share an operatorId across apps, so the item value includes appId
function operatorKey(operator: OperatorApp): string {
  return `${operator.operatorId}:${operator.appId}`;
}

// Popover + Command instead of SearchableSelect: the search input sits above the
// scrolling list, so items never show behind it while scrolling.
export function OperatorCombobox({
  operators,
  selected,
  onSelect,
  isLoading = false,
  disabled = false,
}: OperatorComboboxProps) {
  const [open, setOpen] = useState(false);
  const selectedKey = selected ? operatorKey(selected) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select operator"
          disabled={isLoading || disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {isLoading ? 'Loading operators…' : selected?.name ?? 'Select an operator…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name or operator ID…" />
          <CommandList className="max-h-64">
            <CommandEmpty>No operators found</CommandEmpty>
            <CommandGroup>
              {operators.map((operator) => {
                const key = operatorKey(operator);
                return (
                  <CommandItem
                    key={key}
                    value={key}
                    keywords={[operator.name, String(operator.operatorId)]}
                    onSelect={() => {
                      onSelect(operator);
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{operator.name}</span>
                        <span className="text-xs text-gray-500">ID: {operator.operatorId}</span>
                      </div>
                      {selectedKey === key && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
